import { useCallback, useEffect, useRef } from 'react';

import { bind } from 'bind-event-listener';

import { getDocument } from '@atlaskit/browser-apis';
import type { IUseAnalytics } from '@atlassian/conversation-assistant-instrumentation/types/analytics';

import { useWindowLocation } from '../use-window-location/useWindowLocation';
import { parseRAWTrackedUrls } from './parse-raw-tracked-urls';

const SENTINEL_ATTR = 'data-rovo-chat-copy-id';

type RegisterCopyArgs = {
	messageId: string | undefined;
	analyticsMessageActions: string[] | undefined;
	trackAIMAUEvent: IUseAnalytics['trackAIMAUEvent'];
	interactionModeExecuted?: string;
	deferClipboardReadToNextTask?: boolean;
};

type UseCopyPasteAdoptedResult = {
	registerCopy: (args: RegisterCopyArgs) => void;
};

export type UseCopyPasteAdoptedOptions = {
	usePersistentPasteListener?: boolean;
};

// Bridge-owned copies must keep their paste listener after the modal's hook unmounts.
// Share its cleanup across hook instances so the next copy can replace it.
let removePersistentPasteListener: (() => void) | null = null;

/**
 * Hook that, when a copy action is taken from Rovo chat, appends an invisible
 * sentinel element to the clipboard HTML and listens for a subsequent paste
 * event on the same page. If the paste is verified to originate from this chat
 * session (via the sentinel) and the target page URL resolves to a known
 * Atlassian object type, fires an `aiResult adopted` analytics event.
 */
export function useCopyPasteAdopted({
	usePersistentPasteListener = false,
}: UseCopyPasteAdoptedOptions = {}): UseCopyPasteAdoptedResult {
	// Stores the current sentinel ID — set on copy, cleared after paste or unmount
	const sentinelIdRef = useRef<string | null>(null);
	// Stores the cleanup fn for the active paste listener
	const removeListenerRef = useRef<(() => void) | null>(null);

	const location = useWindowLocation();
	// Mirror `location` in a ref so the paste listener (created at copy-time)
	// always reads the *current* URL when the user actually pastes, instead
	// of the URL that was active when registerCopy was invoked. The user
	// virtually always navigates between copying in Rovo chat and pasting
	// into the destination Atlassian object, so without this the parsed URL
	// would be stale (and typically resolve to `null`, suppressing the event).
	const locationRef = useRef(location);
	useEffect(() => {
		locationRef.current = location;
	}, [location]);

	// Clean up any pending listener on unmount
	useEffect(() => {
		return () => {
			if (!usePersistentPasteListener) {
				removeListenerRef.current?.();
			}
		};
	}, [usePersistentPasteListener]);

	const registerCopy = useCallback(
		({
			messageId,
			analyticsMessageActions,
			trackAIMAUEvent,
			interactionModeExecuted,
			deferClipboardReadToNextTask = false,
		}: RegisterCopyArgs): void => {
			// Remove any existing pending paste listener before registering a new one
			if (usePersistentPasteListener) {
				removePersistentPasteListener?.();
				removePersistentPasteListener = null;
			} else {
				removeListenerRef.current?.();
				removeListenerRef.current = null;
			}

			// Generate a unique sentinel ID for this copy event
			const sentinelId = crypto.randomUUID();
			sentinelIdRef.current = sentinelId;

			// Append our sentinel to the clipboard HTML asynchronously.
			// We read the current clipboard HTML content first, then rewrite it
			// with the sentinel appended. If the clipboard doesn't support HTML
			// (e.g. only text was written), we skip the sentinel — the paste
			// listener will simply not match and will clean itself up.
			void (async () => {
				try {
					if (deferClipboardReadToNextTask) {
						// A copy event can reach registerCopy before the browser writes the selection.
						// Read the clipboard in the next task so that write can complete first.
						await new Promise<void>((resolve) => setTimeout(resolve, 0));
					}
					const clipboardItems = await navigator.clipboard.read();
					for (const item of clipboardItems) {
						if (item.types.includes('text/html')) {
							const htmlBlob = await item.getType('text/html');
							const htmlText = await htmlBlob.text();
							const sentinel = `<span ${SENTINEL_ATTR}="${sentinelId}" style="font-size:0;display:inline-block;width:0;height:0;overflow:hidden"></span>`;
							const newHtml = htmlText + sentinel;

							// Preserve text/plain if present
							let textBlob: Blob | undefined;
							if (item.types.includes('text/plain')) {
								textBlob = await item.getType('text/plain');
							}

							const newItem = textBlob
								? new ClipboardItem({
										'text/html': new Blob([newHtml], { type: 'text/html' }),
										'text/plain': textBlob,
									})
								: new ClipboardItem({
										'text/html': new Blob([newHtml], { type: 'text/html' }),
									});

							await navigator.clipboard.write([newItem]);
							break;
						}
					}
				} catch {
					// If we can't read/write the clipboard (e.g. permissions denied,
					// or only plain text was copied), just skip the sentinel write.
					// The paste listener will not find a matching sentinel and will
					// clean itself up harmlessly.
				}

				// Register paste listener regardless — if clipboard read failed,
				// the sentinel won't match and the listener removes itself cleanly.
				const handlePaste = (event: ClipboardEvent) => {
					// The persistent listener may outlive this hook and its refs.
					const currentSentinelId = usePersistentPasteListener ? sentinelId : sentinelIdRef.current;
					const pasteLocation = usePersistentPasteListener
						? window.location.href
						: locationRef.current;

					// Always remove listener after first paste attempt
					if (usePersistentPasteListener) {
						removePersistentPasteListener?.();
						removePersistentPasteListener = null;
					} else {
						removeListenerRef.current?.();
						removeListenerRef.current = null;
					}
					sentinelIdRef.current = null;

					if (!currentSentinelId) {
						return;
					}

					// Only fire if the paste landed in an editable element — this rules
					// out accidental pastes into non-editable areas (e.g. the address bar,
					// search inputs that are not content editors, read-only sections).
					const target = event.target as HTMLElement | null;
					const isEditable =
						target?.isContentEditable ||
						target?.getAttribute?.('contenteditable') === 'true' ||
						target?.tagName === 'TEXTAREA' ||
						target?.tagName === 'INPUT';
					if (!isEditable) {
						return;
					}

					// Don't fire if the paste landed inside the Rovo chat panel itself
					// (e.g. the user pasted back into the chat input), or into a search
					// input — neither of these constitutes a modification of an Atlassian
					// content object.
					const isInsideChatPanel = !!target?.closest?.('[data-rovo-chat-panel]');
					const isSearchInput =
						target?.getAttribute?.('role') === 'search' ||
						(target as HTMLInputElement | null)?.type === 'search';
					if (isInsideChatPanel || isSearchInput) {
						return;
					}

					const html = event.clipboardData?.getData('text/html') ?? '';

					// Validate that this paste originated from our chat copy
					if (!html.includes(`${SENTINEL_ATTR}="${currentSentinelId}"`)) {
						return;
					}

					// Parse the current page URL (read at PASTE time, not copy time) to
					// identify the Atlassian object the user is pasting into.
					const rovoModifiedObject = parseRAWTrackedUrls(pasteLocation);
					if (!rovoModifiedObject) {
						// Not on a recognized Atlassian product page — don't fire
						return;
					}

					trackAIMAUEvent({
						type: 'adopted',
						actionKey: 'copy',
						rovoModifiedObjects: [rovoModifiedObject],
						proactive: false,
						messageId,
						messageActions: analyticsMessageActions,
						singleInstrumentationID: messageId,
						...(interactionModeExecuted ? { attributes: { interactionModeExecuted } } : {}),
					});
				};

				const doc = getDocument();
				if (doc) {
					const unbind = bind(doc, {
						type: 'paste',
						listener: handlePaste,
						options: { capture: true },
					});
					const removePasteListener = () => {
						unbind();
						sentinelIdRef.current = null;
					};
					if (usePersistentPasteListener) {
						removePersistentPasteListener = removePasteListener;
					} else {
						removeListenerRef.current = removePasteListener;
					}
				}
			})();
		},
		// `location` is read via locationRef at paste time, so this callback
		// can be stable across navigations.
		[usePersistentPasteListener],
	);

	return { registerCopy };
}
